import React, { useState, useRef, useEffect, useId } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

const TIMEFRAME_DROPDOWN_OPENED_EVENT = 'tradeproof:timeframe-dropdown-opened';

interface TimeframeDropdownProps {
  /** The currently selected value */
  value: string;
  /** Callback when a new range is selected */
  onChange: (range: string) => void;
  /** Optional class overrides */
  className?: string;
  /** Reports open state so the owning widget can raise its stacking layer. */
  onOpenChange?: (isOpen: boolean) => void;
}

export const TimeframeDropdown: React.FC<TimeframeDropdownProps> = ({ 
  value, 
  onChange,
  className = '',
  onOpenChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownId = useId();

  // The simplified list with "Last" prefix
  const ranges = [
    'Last 24 Hours', 
    'Last 7 Days', 
    'Last 30 Days', 
    'Last 12 Months', 
    'YTD'
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleAnotherDropdownOpened = (event: Event) => {
      const openedId = (event as CustomEvent<{ id?: string }>).detail?.id;
      if (openedId && openedId !== dropdownId) setIsOpen(false);
    };

    window.addEventListener(TIMEFRAME_DROPDOWN_OPENED_EVENT, handleAnotherDropdownOpened);
    return () => window.removeEventListener(TIMEFRAME_DROPDOWN_OPENED_EVENT, handleAnotherDropdownOpened);
  }, [dropdownId]);

  useEffect(() => {
    onOpenChange?.(isOpen);
  }, [isOpen, onOpenChange]);

  const toggleMenu = () => {
    const nextOpen = !isOpen;
    if (nextOpen) {
      window.dispatchEvent(new CustomEvent(TIMEFRAME_DROPDOWN_OPENED_EVENT, {
        detail: { id: dropdownId },
      }));
    }
    setIsOpen(nextOpen);
  };

  return (
    <div className={`relative z-[2147483647] ${className}`} ref={containerRef}>
      {/* Trigger Button */}
      <button 
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); toggleMenu(); }}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        className={`flex items-center gap-1 px-2 py-1 text-xs rounded border transition-colors
          ${isOpen
            ? 'bg-zinc-50 dark:bg-zinc-600 border-zinc-300 dark:border-zinc-600 text-zinc-800 dark:text-zinc-100'
            : 'bg-zinc-100 dark:bg-zinc-800 border-zinc-300 dark:border-zinc-600 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-600'}
        `}
      >
        <span className="truncate">{value}</span>
        {isOpen ? (
          <ChevronUp className="w-3 h-3 text-zinc-500 dark:text-zinc-400" />
        ) : (
          <ChevronDown className="w-3 h-3 text-zinc-500 dark:text-zinc-400" />
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          role="menu"
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          className="absolute top-full right-0 mt-2 w-44 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg shadow-lg overflow-hidden z-[2147483647] flex flex-col py-1 animate-in fade-in zoom-in-95 duration-100"
          style={{ fontSize: '14px' }}
        >
          {ranges.map((range) => (
            <button 
              key={range}
              role="menuitem"
              onPointerDown={(e) => {
                // Ensure the selection registers before any global click-away handlers run.
                e.preventDefault();
                e.stopPropagation();
                onChange(range);
                setIsOpen(false);
              }}
              onClick={() => {
                onChange(range);
                setIsOpen(false);
              }}
              className={`px-3 py-2 text-left transition-colors flex items-center justify-between
                ${value === range
                  ? 'text-zinc-900 dark:text-zinc-100 font-medium bg-zinc-100 dark:bg-zinc-800'
                  : 'text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800'}
              `}
            >
              <span className="truncate">{range}</span>
              {value === range && (
                <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default TimeframeDropdown;
